import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFile,
  faFileImage,
  faFilePdf,
  faFileCsv,
  faFileVideo,
  faFileWord,
  faFileExcel,
  faFilePowerpoint,
  faFileCode,
  faFileLines,
  faFileZipper,
  faFileAudio,
} from '@fortawesome/free-solid-svg-icons';

export type MimeType =
  | "image/jpeg"
  | "image/png"
  | "application/pdf"
  | "text/csv"
  | "video/mp4"
  | "video/quicktime"
  | "video/x-matroska"
  | "video/webm"
  | "application/octet-stream"
  | "application/msword"
  | "application/vnd.ms-excel"
  | "application/vnd.ms-powerpoint"
  | "image/gif"
  | "image/webp"
  | "text/plain"
  | "text/markdown"
  | "application/rtf"
  | "application/json"
  | "application/xml"
  | "application/zip"
  | "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  | "application/vnd.ms-excel.sheet.macroEnabled.12"
  | "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  | "application/vnd.openxmlformats-officedocument.presentationml.presentation"
  | "application/vnd.oasis.opendocument.text"
  | "application/vnd.oasis.opendocument.spreadsheet"
  | "application/vnd.oasis.opendocument.presentation"
  | "audio/mpeg"
  | "audio/mp4"
  | "audio/aac"
  | "audio/wav"
  | "audio/ogg"
  | "audio/flac";

interface MimeTypeIconProps {
  mimeType: MimeType;
  className?: string;
}

export const MimeTypeIcon: React.FC<MimeTypeIconProps> = ({ mimeType, className = "w-5 h-5" }) => {
  const getIcon = () => {
    switch (mimeType) {
      case "image/jpeg":
      case "image/png":
      case "image/gif":
      case "image/webp":
        return { icon: faFileImage, color: "text-blue-600" };

      case "application/pdf":
        return { icon: faFilePdf, color: "text-red-600" };

      case "text/csv":
        return { icon: faFileCsv, color: "text-green-600" };

      case "video/mp4":
      case "video/quicktime":
      case "video/x-matroska":
      case "video/webm":
        return { icon: faFileVideo, color: "text-purple-600" };

      case "application/msword":
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      case "application/vnd.oasis.opendocument.text":
        return { icon: faFileWord, color: "text-blue-700" };

      case "application/vnd.ms-excel":
      case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      case "application/vnd.ms-excel.sheet.macroEnabled.12":
      case "application/vnd.oasis.opendocument.spreadsheet":
        return { icon: faFileExcel, color: "text-green-700" };

      case "application/vnd.ms-powerpoint":
      case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      case "application/vnd.oasis.opendocument.presentation":
        return { icon: faFilePowerpoint, color: "text-orange-600" };

      case "text/plain":
      case "text/markdown":
      case "application/rtf":
        return { icon: faFileLines, color: "text-gray-700" };

      case "application/json":
      case "application/xml":
        return { icon: faFileCode, color: "text-gray-700" };

      case "audio/mpeg":
      case "audio/mp4":
      case "audio/aac":
      case "audio/wav":
      case "audio/ogg":
      case "audio/flac":
        return { icon: faFileAudio, color: "text-pink-600" };

      case "application/zip":
        return { icon: faFileZipper, color: "text-yellow-700" };

      case "application/octet-stream":
      default:
        return { icon: faFile, color: "text-gray-600" };
    }
  };

  const { icon, color } = getIcon();

  return (
    <FontAwesomeIcon
      icon={icon}
      className={`${className} ${color}`}
    />
  );
};